import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import { DataOptions } from "vuetify";
import { FILTER_NAMES } from "@/static/filterNames";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { CostHint } from "@/models/api/administration/servicesAndAttributes/CostHint";
import {
  addCostHint,
  editCostHint,
  getCostHint,
  getCostHints,
  getCostHintsForEditor,
  removeCostHint,
  updateCostHintCounter,
} from "@/requests/administration/servicesAndAttributes/costHints";
import { CostHintEdit } from "@/models/api/administration/servicesAndAttributes/CostHintEdit";
import { CostHintSelect } from "@/models/api/administration/servicesAndAttributes/CostHintSelect";
import { COST_HINT_TYPE } from "@/static/costHintTypeEnum";

export interface CostHintsStateInterface {
  isLoading: boolean;
  costHintTableData: Array<CostHint>;
  costHintTableCount: number;
  costHintTableOptions: DataOptions;
  costHintTableFilter: Array<CriterionItemRequest> | null;

  isEditDialog: boolean;
  editModel: CostHintEdit;

  hints: Array<CostHintSelect>;
}

export const CostHintsState: CostHintsStateInterface = {
  isLoading: false,
  costHintTableData: [],
  costHintTableCount: -1,
  costHintTableOptions: new DefaultDataOptions(),
  costHintTableFilter: null,

  isEditDialog: false,
  editModel: new CostHintEdit(),

  hints: [],
};

const CostHintsModule = defineModule({
  state: CostHintsState as CostHintsStateInterface,
  namespaced: true as const,
  modules: {},
  getters: {},
  mutations: {
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setCostHintsTableData(state, param: Array<CostHint>) {
      state.costHintTableData = param;
    },
    setCostHintsTableCount(state, param: number) {
      state.costHintTableCount = param;
    },
    setCostHintsTableOptions(state, param: DataOptions) {
      state.costHintTableOptions = param;
    },
    setCostHintsTableFilter(state, param: null | Array<CriterionItemRequest>) {
      state.costHintTableFilter = param;
    },

    setIsEditDialog(state, param: boolean) {
      state.isEditDialog = param;
    },
    setEditModel(state, param: CostHintEdit) {
      state.editModel = param;
    },

    setHints(state, param: Array<CostHintSelect>) {
      state.hints = param;
    },
  },
  actions: {
    async getCostHints(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      const response = await getCostHints({
        count: state.costHintTableOptions.itemsPerPage,
        page: state.costHintTableOptions.page - 1,
        criterions: state.costHintTableFilter,
        filterName: FILTER_NAMES.CostHintFilter,
      });
      commit.setCostHintsTableCount(response.totalCount);
      commit.setCostHintsTableData(response.items);
      commit.setIsLoading(false);
    },
    async openEditDialog(context, id: number | null) {
      const { commit } = _moduleActionContext(context);
      let model = new CostHintEdit();
      if (id) model = await getCostHint(id);
      commit.setEditModel(model);
      commit.setIsEditDialog(true);
    },
    async saveCostHint(context) {
      const { commit, state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (state.editModel.id) await editCostHint(state.editModel);
      else await addCostHint(state.editModel);
      await dispatch.getCostHints();
      await rootDispatch.popup.addSavedOkSnackbar();
      commit.setIsEditDialog(false);
    },
    async removeCostHint(context, id: number) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      if (await rootDispatch.popup.addConfirmRemoveElement()) {
        await removeCostHint(id);
        await dispatch.getCostHints();
        await rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async getCostHintsForEditor(context, costHintTypeEnum: COST_HINT_TYPE) {
      const { commit } = _moduleActionContext(context);
      const response = await getCostHintsForEditor(costHintTypeEnum);
      commit.setHints(response);
    },
    async updateCostHintCounter(context, id: number) {
      await updateCostHintCounter(id);
    },
  },
});
export default CostHintsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, CostHintsModule);